import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import ScrollReveal from "../components/ScrollReveal";
import { MapPin, Maximize2, ArrowRight } from "lucide-react";
import { allProjects, statusConfig } from "../data/projects";

const tabs = [
  { key: "all", label: "All Projects" },
  { key: "ongoing", label: "Ongoing" },
  { key: "completed", label: "Completed" },
  { key: "upcoming", label: "Upcoming" },
];

export default function Projects() {
  const [activeTab, setActiveTab] = useState("all");

  const filtered = activeTab === "all" ? allProjects : allProjects.filter((p) => p.status === activeTab);

  return (
    <Layout>
      {/* Page Header */}
      <section
        className="relative py-16 md:py-24 text-white bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1561986417-417e3bce0b57?w=1600&h=600&fit=crop')" }}
      >
        <div className="absolute inset-0 bg-primary/80" />
        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <div className="max-w-3xl">
              <h1 className="text-5xl md:text-6xl font-bold mb-4">Our Projects</h1>
              <p className="text-xl text-gray-200">
                Explore our portfolio of residential and commercial projects across Mumbai, Navi Mumbai, and beyond.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Filter Tabs + Projects Grid */}
      <section className="py-10 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="project-filter-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                data-testid={`filter-tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${
                  activeTab === tab.key
                    ? "bg-primary text-white shadow-md"
                    : "bg-white text-foreground border border-border hover:border-primary hover:text-primary"
                }`}
              >
                {tab.label}
                <span className="ml-2 text-xs opacity-70">
                  ({tab.key === "all" ? allProjects.length : allProjects.filter((p) => p.status === tab.key).length})
                </span>
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="projects-grid">
            {filtered.map((project, index) => (
              <ScrollReveal key={project.id} delay={index * 0.07}>
                <Link
                  to={`/projects/${project.slug}`}
                  data-testid={`project-card-${project.id}`}
                  className="block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group border border-border"
                >
                  <div className="h-52 overflow-hidden relative">
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusConfig[project.status].color}`}>
                        {statusConfig[project.status].label}
                      </span>
                    </div>
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-primary/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="flex items-center gap-2 text-white font-semibold bg-secondary/90 px-4 py-2 rounded-full text-sm">
                        View Details <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-bold text-primary mb-2 group-hover:text-secondary transition-colors">{project.name}</h3>
                    <div className="flex items-start gap-2 text-muted-foreground text-sm mb-3">
                      <MapPin size={14} className="flex-shrink-0 mt-0.5 text-secondary" />
                      <span>{project.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Maximize2 size={14} className="text-secondary" />
                      <span>Area: {project.area}</span>
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">No projects found in this category.</div>
          )}
        </div>
      </section>
    </Layout>
  );
}
